import { useEffect, useState } from "react"
import { Country } from "../shared/types"
import { HeaderImage } from "../components/HeaderImage"
import { getImageFromStrapiUploads, getPrimaryColor } from "../shared/utils"
import Link from "next/link"
import { destination_texts } from "../assets/texts/texts"
import { useRouter } from "next/router"
import { useTranslation } from "next-i18next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { GET_COUNTRIES } from "../assets/api/apiCalls"
import Head from "next/head"
import { GetStaticProps } from "next"
import { getApolloClient } from "./_app"
import dynamic from "next/dynamic"
import { Icon } from "@iconify/react"
import { pageTitle, sectionTitle } from "../components/misc/titles"

const WorldMap = dynamic(() => import("react-svg-worldmap"), { ssr: false })

interface DestinationsProps {
    destinationsData: any
    continentsData: string[]
    error?: string
}

export default function AllDestinations({ destinationsData: initialCountriesData, continentsData: initialContinentsData, error }: DestinationsProps) {
    const [countriesData, setCountriesData] = useState(initialCountriesData)
    const [countriesLoading, setCountriesLoading] = useState(false)
    const router = useRouter()
    const [uniqueContinents, setUniqueContinents] = useState<string[]>(initialContinentsData)
    const [deviceSize, setDeviceSize] = useState<"sm" | "lg" | "md" | "responsive" | number >()
    const { locale } = router.locale ? router : { locale: 'it' }
    const primaryColor = getPrimaryColor()
    const { t } = useTranslation()
    const client = getApolloClient()

    useEffect(() => {
        const fetchData = async () => {
            setCountriesLoading(true)
            if (locale !== countriesData?.posts?.data?.[0]?.attributes?.locale) {
                const data = await fetchDestinationsData(locale, client)
                if (data) {
                    setCountriesData(data)
                }
            }
            setCountriesLoading(false)
        }
        fetchData()
    }, [locale])

    useEffect(() => {
        const fetchData = (countriesData: any) => {
            const uniqueContinents = fetchContinents(countriesData)
            setUniqueContinents(uniqueContinents)
        }
        fetchData(countriesData)
    }, [countriesData])

    useEffect(() => {
        const updateDeviceSize = () => {
            const width = window.innerWidth
            let newSize: any
            if (width > 660) {
                newSize = "responsive"
            } else if (width > 500 && width <= 660) {
                newSize = "lg"
            } else if (width > 330 && width <= 500) {
                newSize = "md"
            } else {
                newSize = "sm"
            }
            setDeviceSize(newSize)
        }

        updateDeviceSize()
        window.addEventListener('resize', updateDeviceSize)
        return () => {
            window.removeEventListener('resize', updateDeviceSize)
        }
    }, [deviceSize])

    const mapOnClick = (event: any) => {
        const clickedCountryCode = event.countryCode.toLowerCase()
        const clickedCountry = countriesData?.countries?.data?.find((country: Country) => country?.attributes?.isoCode.toLowerCase() === clickedCountryCode)
        if (clickedCountry) {
            const countryName = clickedCountry?.attributes?.name.toLowerCase()
            router.push(`/country/${countryName}`)
        }
    }

    const getPageInOtherLanguages = (locale: string) => {
        if (locale === 'en') {
            return (
                <>
                    <link rel="alternate" href="https://explorerstale.com/destinations" hrefLang="it" />
                </>
            )
        } else {
            return (
                <>
                    <link rel="alternate" href="https://explorerstale.com/en/destinations" hrefLang="en" />
                </>
            )
        }
    }

    const loadingAnimation = () => {
        return (
            <div className="max-w-[80rem] bg-white p-4 rounded animate-pulse mx-auto">
                <div className="h-4 w-full h-[3rem] mb-2 bg-gray-300 dark:text-gray-300 rounded"></div>
                <div className="h-4 w-3/4 bg-gray-300 dark:text-gray-300 rounded"></div>
                <ul role="list" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 justify-center gap-5 mb-5 mt-5">
                    <div className="h-4 w-full h-[10rem] bg-gray-300 dark:text-gray-300 rounded"></div>
                    <div className="h-4 w-full h-[10rem] bg-gray-300 dark:text-gray-300 rounded"></div>
                    <div className="h-4 w-full h-[10rem] bg-gray-300 dark:text-gray-300 rounded"></div>
                    <div className="h-4 w-full h-[10rem] bg-gray-300 dark:text-gray-300 rounded"></div>
                </ul>
            </div>
        )
    }

    const getMetadata = () => {
        return (
            <Head>
                <title>{t("common.destinations")}</title>
                <meta name="description" content={t("meta.index_description")} />
                <meta property="og:title" content={`${t("common.destinations")} | Explorer's Tale`} />
                <meta property="og:image" content={`https://explorerstale.com/assets/destinations.jpg`} />
                <meta property="og:description" content={t("meta.index_description")} />
                <meta property="og:url" content={locale !== "it" ? `https://explorerstale.com/${locale}/destinations` : `https://explorerstale.com/destinations`} />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content={locale} />
                <meta property="og:site_name" content="Explorer's Tale" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@explorerstale" />
                <meta name="twitter:creator" content="@explorerstale" />
                <meta name="twitter:title" content={`${t("common.destinations")} | Explorer's Tale`} />
                <meta name="twitter:description" content={t("meta.index_description")} />
                <meta name="twitter:image" content={`https://explorerstale.com/assets/destinations.jpg`} />
                <link rel="canonical" href={locale !== "it" ? `https://explorerstale.com/${locale}/destinations` : `https://explorerstale.com/destinations`} />
                <meta name="author" content="Alessio Lucciola" />
                <meta name="robots" content="all" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <meta name="content-language" content={locale} />
                <meta name="keywords" content={t("meta.index_keywords")} />
                {getPageInOtherLanguages(locale)}
            </Head>
        )
    }

    return (
        <>
            {getMetadata()}
            <HeaderImage image="/assets/destinations.jpg" alt={`destinations-cover`} texts={destination_texts}/>
            {countriesLoading ? (
                loadingAnimation()
            ) : (
                <>
                    <div className='relative max-w-[80rem] mx-auto mt-5 sm:mt-5 font-poppins'>
                        <div className='flex flex-col mx-auto mr-5 ml-5 justify-center gap-2'>
                            <div className="flex flex-col gap-3 pt-2 text-sm text-gray-400">
                                <>
                                {pageTitle({title: t('common.destinations')})}
                                {countriesData?.countries?.data?.length !== 0 ? (
                                    <>
                                        <p className="leading-8 text-gray-600">{t('destination.explore_destinations', { destinations_num: countriesData?.countries?.data?.length })}</p>
                                        {uniqueContinents.map(continent => (
                                            <div key={continent} className="flex flex-col gap-2">
                                                {sectionTitle({title: continent})}
                                                <ul role="list" className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 justify-center gap-5 mb-5 transition-opacity duration-500 ${countriesData?.countries?.data?.length ? 'opacity-100' : 'opacity-0'}`}>
                                                    {countriesData?.countries?.data?.filter((country: Country) => country?.attributes?.continent?.data?.attributes?.name === continent).sort((a: Country, b: Country) => a?.attributes?.name.localeCompare(b?.attributes?.name)).map((country: Country) => (
                                                        <DestinationCard key={country.id} country={country} />
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                        <div className="flex justify-center">
                                            {countriesData?.countries !== undefined && (
                                                <WorldMap color={primaryColor} value-suffix="" size={deviceSize} richInteraction data={countriesData?.countries?.data?.map((country: Country) => ({ country: country?.attributes?.isoCode, value: " " }))} onClickFunction={mapOnClick}/>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex flex-row gap-1 items-starttext-md bg-gray-200 p-1 rounded-xl text-black">
                                        <span className="text-2xl">
                                            <Icon icon="mdi:face-sad-outline" />
                                        </span>
                                        <p>{t("destination.no_destinations")}</p>
                                    </div>
                                )}
                                </>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    )
}

export const DestinationCard = ({country}: {country: Country}) => {
    const { t } = useTranslation()

    return (
        <li key={country?.attributes?.name} className="relative mx-auto w-full h-auto transition duration-500 ease-in-out transform hover:scale-105">
            <Link href={{ pathname: "/country/[name]", query: { name: country?.attributes?.name?.toLowerCase()} }} className="hover:no-underline">
            <div className="flex flex-col h-[100%] pb-2">
                    <div className='relative'>
                        <img className="h-[13rem] w-full object-cover mx-auto rounded-md" src={country?.attributes?.countryCover?.data !== null ? getImageFromStrapiUploads(country?.attributes?.countryCover?.data?.attributes, "small") : "/assets/no-img.jpg"} alt={country?.attributes?.name+`-cover`} />
                        <div className='absolute bottom-3 left-3 text-left pr-2'>
                            <p className="text-white text-bold text-md uppercase transition duration-300 ease-in-out">{t("country.country")}</p>
                            <p className="font-bold text-white text-bold text-2xl uppercase transition duration-300 ease-in-out">{country?.attributes?.name}</p>
                        </div>
                    </div>
                    <div className="pl-2 pr-2 pt-2">
                        <p className="text-black font-bold text-lg uppercase">{country?.attributes?.name}</p>
                        <div className="text-black text-sm overflow-hidden" style={{ maxHeight: '6rem', lineHeight: '1.5', textOverflow: 'ellipsis' }}>
                            {country?.attributes?.shortDescription}
                        </div>
                    </div>
                </div>
            </Link>
        </li>
    )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
    const client = getApolloClient()

    try {
        const destinationsData = await fetchDestinationsData(locale, client)
        const uniqueContinents = fetchContinents(destinationsData)

        return {
            props: {
                ...(await serverSideTranslations(locale, ['common', 'destination'])),
                destinationsData: destinationsData || null,
                continentsData: uniqueContinents,
            }
        }
    } catch (error) {
        console.error("Error fetching destinations data:", error)

        return {
            props: {
                ...(await serverSideTranslations(locale, ['common', 'destination'])),
                destinationsData: null,
                uniqueContinents: [],
                error: 'Failed to fetch destinations data',
            },
        }
    }
}

const fetchDestinationsData = async (locale: string, client: any) => {
    try {
        const { data } = await client.query({
            query: GET_COUNTRIES,
            variables: {
                "locale": locale,
                "limit": 200
            }
        })

        return data
    } catch (error) {
        console.error("Error fetching countries data:", error)
        return null
    }
}

const fetchContinents = (countriesData: any) => {
    if (countriesData?.countries?.data?.length > 0) {
        let uniqueContinents: string[] = Array.from(new Set(countriesData?.countries?.data?.map((country: { attributes: { continent: { data: { attributes: { name: string } } } } }) => country?.attributes?.continent?.data?.attributes?.name)))

        uniqueContinents = uniqueContinents.sort((a: string, b: string) => a.localeCompare(b))
        return uniqueContinents === undefined || uniqueContinents.length === 0 || uniqueContinents[0] === undefined ? [] : uniqueContinents
    }
    return []
}